<template>
  <div id="content" :class="{'bg-white': isWhitePage, 'max-width': !isCoachingPlatform}">
    <transition name="fade" mode="out-in">
      <div
        v-if="sessionBookingLimitReached"
        class="session-booking-error d-flex flex-column align-items-center justify-content-center bg-white"
      >
        <div class="handover-main session-limit-main">
          <i class="fa-light fa-circle-exclamation"></i>
          <p class="handover-paragraph handover-paragraph-top">
            You have reached your available session limit.
          </p>
          <p class="handover-paragraph">
            Please reach out to your employer for more details.
          </p>
          <b-button variant="continue" @click="$router.push('/')">
            <span>Return to home</span>
          </b-button>
        </div>
      </div>
      <div
        v-else-if="sessionBookingErrorMessage"
        class="session-booking-error d-flex flex-column align-items-center justify-content-center bg-white"
      >
        <h3 class="dmh-start-title brand-text pb-4 mb-3">Service not available</h3>
        <p class="brand-text pb-5 mb-0 text-center h4">{{ sessionBookingErrorMessage }}</p>
        <b-button
          variant="cta"
          size="lg"
          id="return-to-home-button"
          class="py-4 px-5"
          squared
          @click="$router.push('/')"
        >Return to portal</b-button>
      </div>
      <component v-else @scroll-to="scrollTo" :is="currentContent" v-bind="currentProps"/>
    </transition>
  </div>
</template>

<script>

import Dmh from "@/pages/Dmh.vue"
import DmhStart from "@/pages/DmhStart.vue"
import DmhFaqs from "../pages/DmhFaqs.vue";
import DmhArticles from "../pages/DmhArticles.vue";
import DmhFinance from "../pages/DmhFinance.vue";
import DmhF2F from "../pages/DmhF2F.vue";
import DmhMessage from "../pages/DmhMessage.vue";
import MyHealth from "../pages/MyHealth.vue";
import MyHealthArea from "../pages/MyHealthArea.vue";
import MyHealthHandover from "../pages/MyHealthHandover.vue";
import PaymentConfirmation from "../pages/PaymentConfirmation.vue";
import DmhTransactionCancelled from "../pages/DmhTransactionCancelled.vue";
import CoachingPlatform from "./TMH/CoachingPlatform.vue";
import BookingVideoRoom from "./TMH/VideoRoomWithCoach/BookingVideoRoom.vue";
import SessionBooking from "@journeys/components/SessionBooking.vue";
import config from "@/config/constants";
import axios from "axios";
import { redirectHandler } from "@/utils/RedirectHandler";


const mappings = {
  home: Dmh,
  dmh: Dmh,
  dmhStart: DmhStart,
  paymentConfirmation: PaymentConfirmation,
  dmhTransactionCancelled: DmhTransactionCancelled,
  dmhFinance: DmhFinance,
  dmhFaqs: DmhFaqs,
  dmhArticles: DmhArticles,
  dmhF2F: DmhF2F,
  dmhMessage: DmhMessage,
  myHealth: MyHealth,
  myHealthArea: MyHealthArea,
  myHealthHandover: MyHealthHandover,
  coachingPlatform: CoachingPlatform,
  bookingVideoRoom: BookingVideoRoom,
};


export default {
  name: "ContentRouter",
  data() {
    return {
      sessionBookingApiError: null,
      sessionBookingLimitReached: false,
    };
  },
  watch: {
    "$route.query.service_id": {
      immediate: true,
      handler() {
        this.sessionBookingApiError = null;
        this.sessionBookingLimitReached = false;
      },
    },
  },
  methods: {
    scrollTo(coordinates) {
      this.$emit("scroll-to", coordinates);
    },
  },
  computed: {
    navType() {
      return this.$store.getters.navType;
    },
    isVpcJourney() {
      return (
        this.$route.name === "dmhStart" &&
        this.$route.query?.service_id === "vpc"
      );
    },
    currentContent() {
      if (this.isVpcJourney) return SessionBooking;
      return mappings[this.$route.name];
    },
    isWhitePage(){
      let whitePages = ['dmh', 'dmhStart', 'dmhFaqs', 'dmhArticles','dmhFinance', 'dmhF2F', 'dmhMessage'];
      return whitePages.includes(this.$route.name)
    },
    isCoachingPlatform() {
      return this.$route.name === 'coachingPlatform'
    },
    sessionBookingErrorMessage() {
      if (!this.isVpcJourney) return null;
      if (this.sessionBookingLimitReached) {
        return "You have reached your available session limit. Please reach out to your employer for more details.";
      }
      if (this.sessionBookingApiError) {
        return "We couldn't find this booking service. Please return to the home page and try again.";
      }
      return null;
    },
    currentProps() {
      if (this.isVpcJourney) {
        const userId = this.$store.getters.user_id;
        const post = (path, payload) =>
          axios.post(
            config.api_env + "/application/" + path,
            payload,
            config.options
          );

        return {
          userId,
          serviceType: this.$route.query?.service_id || "vpc",
          apiUrl: config.api_env,

          theme: {},
          fonts: {
            regular: "DMSans",
            bold: "DMSans Bold",
          },

          serviceData: {
            included_in_your_plan: true,
          },

          apiQuestionnaire: {
            getQuestion: async (payload) => {
              const res = await post("api/u/dmh/get-question", payload);
              if (res?.data && res.data.success === false) {
                this.sessionBookingApiError = res.data.message || "questionnaire not found";
              }
              return res;
            },

            answerQuestion: (payload) => post("api/u/dmh/answer-question", payload),

            createCallbackCase: (payload) =>
              post("api/u/call-back-service/create-callback-case", payload),

            getReviewQuestions: (payload) => post("api/u/dmh/get-review-questions", payload),

            getClinicianDates: (payload) => post("api/u/dmh/get-other-clinicians-dates", payload),

            bookAppointment: (payload) => post("api/u/dmh/book-appointment", payload),

            getPaymentLink: (payload) => post("api/u/dmh/get-payment-link", payload),

            getPostcodeAddress: (payload) => post("api/u/postcode/get-address", payload),

            getAddressFromCoordinates: (payload) =>
              post("api/u/dmh/get-address-from-coordinates", { user_id: userId, ...payload }),

            getFeatureFlags: (payload) =>
              post("api/u/feature-flag", { user_id: userId, ...payload }),

            uploadFile: (formData) =>
              axios.post(
                config.api_env + "/application/api/u/file-upload/upload-single",
                formData
              ),

            deleteFile: (payload) => post("api/u/file-upload/delete-s3-file", payload),

            getFileViewUrl: (encodedData) =>
              `${config.api_env}/application/api/u/file-upload/view-file?data=${encodedData}`
          },

          onNavigate: (path) => this.$router.push(path),
          onReturn: redirectHandler,
          termsUrl: "/terms-and-conditions",
          vpcTermsUrl: "/terms-and-conditions-vpc",
        };
      }

      return {};
    }
  },
};
</script>
<style lang="less">
#content {
  display: flex;
  flex: 1 1 auto;
  justify-content: center;
  & > div {
    width: 100%;
  }

  &.max-width {
    width: 100%;
    max-width: 1170px;
    margin: 0 auto;
  }
}
.fade-enter-active {
  transition-duration: 0.5s;
  transition-property: opacity;
  transition-timing-function: ease-in;
}
.fade-leave-active {
  transition-duration: 0s;
  transition-property: opacity;
  transition-timing-function: ease-in;
}
.fade-enter,
.fade-leave-active {
  opacity: 0;
}
.slide-fade-enter-active {
  transition: all 1s ease;
}
.slide-fade-leave-active {
  transition: all 0s ease;
}
.slide-fade-enter
  /* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateX(10px);
  opacity: 0;
}
.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}
</style>
